import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const STATS = [
  { value: "42,000+", label: "Learners graduated" },
  { value: "86%", label: "Role change within 9 months" },
  { value: "310+", label: "Hiring partners" },
  { value: "4.7 / 5", label: "Average learner rating" },
];

const STEPS = [
  { n: "01", title: "Enrol & assess", body: "A short skills check places you at the right starting point — no wasted weeks on material you already know." },
  { n: "02", title: "Learn live", body: "Weekly live sessions with practitioners, backed by self-paced modules you can revisit anytime." },
  { n: "03", title: "Build a capstone", body: "Ship a real project reviewed by industry mentors — the thing you'll actually show in interviews." },
  { n: "04", title: "Get placed", body: "Resume reviews, mock interviews, and warm introductions across our 310+ hiring partners." },
];

const PARTNERS = ["Stripe", "Figma", "Notion", "Vercel", "Linear", "Anthropic", "OpenAI", "Databricks", "GitHub", "Shopify", "Airbnb", "Ramp"];

export default function Landing() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative border-b border-novya-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-32">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-8">
            Cohorts open · September intake
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <h1 className="md:col-span-9 font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tighter text-novya-ink">
              Four disciplines.<br />
              One career leap<span className="text-novya-accent">.</span>
            </h1>
            <p className="md:col-span-8 md:col-start-5 mt-8 md:mt-0 text-base md:text-lg text-novya-muted leading-relaxed max-w-2xl">
              Job-ready programs in software engineering, AI/ML, data science, and product design — built with working practitioners, mentored end to end.
            </p>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              data-testid="hero-browse-btn"
              to="/programs"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-novya-ink text-white font-medium hover:bg-novya-accent transition-all duration-300 hover:-translate-y-0.5"
            >
              Browse programs
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              data-testid="hero-how-btn"
              to="#how"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-novya-ink text-novya-ink font-medium hover:bg-novya-ink hover:text-white transition-colors"
            >
              See how it works
            </Link>
          </div>

          {/* Hero image bento */}
          <div className="mt-20 grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 md:col-span-8 aspect-[16/10] overflow-hidden bg-novya-dark">
              <img
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwc3R1ZGVudHMlMjBjb2RpbmclMjBsYXB0b3B8ZW58MHx8fHwxNzg1NTgzNzUwfDA&ixlib=rb-4.1.0&q=85"
                alt="Learners collaborating"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-6 md:col-span-4 aspect-[4/5] md:aspect-auto overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1535957998253-26ae1ef29506?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd29ya3NwYWNlJTIwc29mdHdhcmUlMjBlbmdpbmVlcmluZ3xlbnwwfHx8fDE3ODU1ODM3NTB8MA&ixlib=rb-4.1.0&q=85"
                alt="Workspace"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="col-span-6 md:hidden aspect-[4/5] bg-novya-ink flex items-end p-6">
              <div className="font-display font-black text-white text-4xl leading-none tracking-tighter">
                Hired<span className="text-novya-accent">.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-novya-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`} className="border-l border-novya-ink pl-6">
              <div className="font-mono text-3xl md:text-5xl font-bold text-novya-ink tracking-tight">{s.value}</div>
              <div className="mt-3 text-sm text-novya-muted leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="border-b border-novya-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted pt-3">
              Programs
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display font-black text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-novya-ink">
                Pick a track. Go deep.
              </h2>
              <p className="mt-6 text-base md:text-lg text-novya-muted max-w-2xl leading-relaxed">
                Every program pairs a structured curriculum with live mentorship and a portfolio-grade capstone — not just video lectures.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-novya-border border border-novya-border">
            {programs.map((p, i) => (
              <Link
                key={p.slug}
                to={`/programs/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="group bg-novya-surface p-8 md:p-12 flex flex-col justify-between hover:bg-novya-ink hover:text-white transition-colors duration-300 min-h-[360px]"
              >
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted group-hover:text-white/50">
                    0{i + 1} · Track
                  </div>
                  <h3 className="mt-6 font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-novya-muted group-hover:text-white/70 max-w-md leading-relaxed">
                    {p.tagline}
                  </p>
                </div>
                <div className="mt-10 flex justify-between items-end">
                  <div className="flex gap-6 font-mono text-xs tracking-wider text-novya-muted group-hover:text-white/50">
                    <span>{p.duration}</span>
                    <span>{p.format}</span>
                  </div>
                  <ArrowUpRight size={22} className="group-hover:rotate-45 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="how" className="border-b border-novya-border bg-novya-dark text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 pt-3">
              Process
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display font-black text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
                From enrolment<br />to offer letter.
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/60 max-w-2xl leading-relaxed">
                The same four-stage path runs under every program, so mentors and career coaches know exactly where you are.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="bg-novya-dark p-8 md:p-10">
                <div className="font-mono text-xs tracking-[0.3em] text-novya-accent">{s.n}</div>
                <h3 className="mt-8 font-display font-bold text-2xl tracking-tight">{s.title}</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE PARTNERS */}
      <section className="border-b border-novya-border py-16 overflow-hidden">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted text-center mb-8">
          310+ hiring partners
        </div>
        <div className="relative overflow-hidden">
          <div className="flex marquee-track gap-24 whitespace-nowrap">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <span key={i} className="font-display font-light text-3xl md:text-5xl tracking-tight text-novya-ink/40">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="border-b border-novya-border">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-novya-muted mb-8">Outcomes</div>
          <blockquote className="font-display font-light text-3xl md:text-5xl leading-[1.15] tracking-tight text-novya-ink max-w-4xl">
            "I switched from a support role into a data analyst position in six months — the mentorship made the difference, not just the videos."
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="w-12 h-12 bg-novya-ink rounded-full flex items-center justify-center text-white font-display font-bold">PN</div>
            <div>
              <div className="font-medium text-novya-ink">Priya N.</div>
              <div className="font-mono text-xs text-novya-muted">Data Science · Graduated 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-novya-bg">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
          <h2 className="font-display font-black text-4xl md:text-6xl lg:text-8xl tracking-tighter leading-[0.95] text-novya-ink max-w-5xl">
            Your next role starts with the next cohort<span className="text-novya-accent">.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-novya-muted max-w-xl">
            Applications close soon for the September intake across all four programs.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              data-testid="cta-apply-btn"
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-novya-accent text-white font-medium hover:bg-novya-accent-hover transition-colors"
              style={{ backgroundColor: "#0047FF" }}
            >
              Apply now <ArrowRight size={18} />
            </Link>
            <Link
              data-testid="cta-programs-btn"
              to="/programs"
              className="inline-flex items-center gap-2 px-8 py-4 border border-novya-ink text-novya-ink font-medium hover:bg-novya-ink hover:text-white transition-colors"
            >
              Talk to an advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
